/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { ReactComponent as SearchIcon } from '../../assets/images/browse-icon-search.svg';
import { ReactComponent as CheckIcon } from '../../assets/images/check.svg';
import { ReactComponent as AddContactIcon } from '../../assets/images/plusWithoutCircleIcon.svg';
import { ReactComponent as SyncIcon } from '../../assets/images/sync-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/images/tabler-icon-chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '../../assets/images/tabler-icon-chevron-up.svg';
import { useGetContactsQuery } from '../../redux/apis/contactsApi';
import { TextInput } from '../shared/TextInput/TextInput';
import { UserAvatar } from '../UserAvatar/UserAvatar';

import styles from './ContactSelect.module.scss';

const CONTACT_SOURCE = {
  google_people: 'Google',
  manual: 'Boon',
  linkedin: 'LinkedIn',
  outlook_contact: 'Outlook',
  boon_contact: 'Boon',
};

export const ContactSelect = ({ onSelect, onConnectNetwork, selectedContact }) => {
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selectedEmail, setSelectedEmail] = useState(selectedContact?.email || '');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(handler);
  }, [query]);

  const { data: contacts } = useGetContactsQuery({ query: debouncedQuery });

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleContactSelect = (contact) => {
    if (contact.emails.length === 1) {
      onSelect?.({ ...contact, email: contact.emails[0] });
      setIsOpen(false);
    } else if (contact.emails.length > 1) {
      const emailToUse = selectedEmail && contact.emails.includes(selectedEmail) ? selectedEmail : contact.emails[0];
      onSelect?.({ ...contact, email: emailToUse });
      setIsOpen(false);
    }

    if (!contact.emails.includes(selectedEmail)) {
      setSelectedEmail(contact.emails[0]);
    }
  };

  const handleEmailSelect = (contact, email) => {
    setSelectedEmail(email);
    onSelect?.({ ...contact, email });
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setExpanded({});
  };

  const handleInputBlur = (e) => {
    if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget)) {
      return;
    }

    setTimeout(() => setIsOpen(false), 200);
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className={styles.container}>
      <TextInput
        data-testid="contact-search-input"
        name="contact-search"
        placeholder={t('placeholders.searchContacts')}
        leftIcon={SearchIcon}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        shouldDisplayLabel={false}
      />

      {isOpen && (
        <div ref={dropdownRef} className={styles.dropdown} onClick={handleDropdownClick}>
          <div className={styles.contactsList}>
            {contacts?.payload?.user_contacts?.map((contact) => (
              <div
                key={contact.id}
                data-testid={`contact-item-${contact.id}`}
                className={classNames(styles.contactItem, {
                  [styles.selected]: contact.emails.includes(selectedEmail),
                })}
                onClick={() => handleContactSelect(contact)}
              >
                <div className={styles.contactHeader}>
                  <div className={styles.contactInfo}>
                    <div className="max-md:hidden">
                      <UserAvatar name={contact.full_name} />
                    </div>
                    <div className="flex items-center gap-[4px]">
                      <div className={styles.name}>{contact.full_name}</div>
                      <span className="md:hidden">• ({contact.source})</span>
                    </div>
                    <div className={styles.details}>
                      <span className="max-md:ml-[4px]">
                        <span className="max-md:hidden">•</span>{' '}
                        {contact.emails.includes(selectedEmail) ? selectedEmail : contact.email}
                      </span>
                      <span className="max-md:hidden">• ({CONTACT_SOURCE[contact.source] || 'Boon'})</span>
                    </div>
                  </div>
                  {contact.emails.length > 1 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded(contact.id);
                      }}
                      className={styles.expandButton}
                    >
                      <span className={styles.expandButtonText}>
                        {t('contacts.moreEmails', { count: contact.emails.length - 1 })}
                      </span>
                      {expanded[contact.id] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </button>
                  )}
                </div>

                {expanded[contact.id] && (
                  <div className={styles.expandedEmails}>
                    {contact.emails.map((email) => (
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <label
                        key={`email-${email.replace(/\s+/g, '-').toLowerCase()}`}
                        className={styles.emailLabel}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="radio"
                          name="selectedEmail"
                          value={email}
                          checked={selectedEmail === email}
                          onChange={() => handleEmailSelect(contact, email)}
                          className={styles.emailRadio}
                        />
                        <span className={styles.customRadio}>{selectedEmail === email && <CheckIcon />}</span>
                        {email}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={styles.actionButtons}>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`${styles.actionButton} ${styles.addContact}`}
            >
              <AddContactIcon className="w-[24px] h-[24px]" /> {t('contacts.addNewContact')}
            </button>
            <button
              type="button"
              onClick={onConnectNetwork}
              className={`${styles.actionButton} ${styles.connectNetwork}`}
            >
              <SyncIcon /> {t('contacts.connectNetwork')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
